import * as React from 'react'
import assert from '../../../../common/src/assert'
import { formatOptPhoneNumber, nullToEmpty } from '../../../../common/src/format'
import {
  ShowingPolicy,
  TourType,
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { Field } from '../components/EditBox'
import EditBoxes from '../components/EditBoxes'

const showingHours: Field<string> = {
  key: 'showingHours',
  label: 'Showing Hours',
  isEditable: false,
}

const areAgentsOnsite: Field<boolean> = {
  key: 'areAgentsOnsite',
  isEditable: true,
  textFunc: areAgentsOnsite => (areAgentsOnsite ? 'On-site agents' : 'Off-site agents'),
  editTextFunc: areAgentsOnsite => (areAgentsOnsite ? 'on' : 'off'),
  validator: validate.options({ on: true, off: false }),
}

const propertyWebsite: Field<string> = EditBoxes.optUrl(
  'propertyWebsite',
  'Property site?',
  'Property: ',
  'https://...'
)

const floorPlans: Field<string> = EditBoxes.optUrl('floorPlansUrl', 'Floor plans?', 'Floor plans: ', 'https://...')

const section8: Field<boolean | null> = {
  key: 'acceptSection8',
  isEditable: true,
  defaultToParentLabel: () => 'Sec 8: see firm',
  overridenByChildLabel: 'Sec 8: see property',
  textFunc: acceptSection8 => (acceptSection8 == null ? 'Sec 8?' : acceptSection8 ? 'Yes sec 8' : 'No sec 8'),
  validator: validate.options({ yes: true, no: false, '': null }),
  placeholder: 'yes or no',
  editTextFunc: val => (val == null ? '' : val ? 'yes' : 'no'),
}

const furnished: Field<boolean | null> = {
  key: 'isFurnished',
  isEditable: true,
  defaultToParentLabel: () => 'Furnished: see firm',
  overridenByChildLabel: 'Furnished: see property',
  textFunc: isFurnished => (isFurnished == null ? 'Furnished?' : isFurnished ? 'Furnished' : 'Unfurnished'),
  validator: validate.options({ yes: true, no: false, '': null }),
  placeholder: 'yes or no',
  editTextFunc: val => (val == null ? '' : val ? 'yes' : 'no'),
}

const seePropertyText = 'see property'

const section8Text: Field<string> = {
  key: 'section8Text',
  label: 'Section8 Text',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'We do (not) accept sec 8 …',
  validator: validate.str().preventEmojis(),
}

const qualText: Field<string> = {
  key: 'qualificationText',
  label: 'Requirements',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'We require that …',
  validator: validate.str().preventEmojis(),
}

const petPolicyText: Field<string> = {
  key: 'petPolicyText',
  label: 'Pet Policy',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'Cats and dogs are …',
  validator: validate.str().preventEmojis(),
}

const moveInCostText: Field<string> = {
  key: 'moveInCostText',
  label: 'Move In Cost',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'Security deposit is 1mo rent, application fee is $50, …',
  validator: validate.str().preventEmojis(),
}

const discountsText: Field<string> = {
  key: 'discountsText',
  label: 'Discounts',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'Military and law enforcement personnel get $100 …',
  validator: validate.str().preventEmojis(),
}

const applicationDocsText: Field<string> = {
  key: 'applicationDocsText',
  label: 'Application Docs',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'We require physical copies of: …',
  validator: validate.str().preventEmojis(),
}

const leaseTermsText: Field<string> = {
  key: 'leaseTermsText',
  label: 'Lease Terms',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: '6 months, 12 months, or 18 months …',
  validator: validate.str().preventEmojis(),
}

const cosignerPolicyText: Field<string> = {
  key: 'cosignerPolicyText',
  label: 'Co-signer Policy',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'We do not require co-signers …',
  validator: validate.str().preventEmojis(),
}

const realtorText: Field<string> = {
  key: 'realtorText',
  label: 'Realtor Policy',
  defaultToParentLabel: () => 'see firm',
  overridenByChildLabel: seePropertyText,
  isEditable: true,
  placeholder: 'We work with realtors …',
  validator: validate.str().preventEmojis(),
}

const officePhone: Field<string> = {
  key: 'officePhone',
  isEditable: true,
  textFunc: v => 'Office phone: ' + (formatOptPhoneNumber(v) || 'missing'),
  defaultToParentLabel: () => 'Office phone: see firm',
  overridenByChildLabel: 'Office phone: see task',
  editTextFunc: v => v || '',
  validator: validate.phone().empty(''),
  tooltip:
    'All landline calls will be forwarded to the office phone, if you ' +
    'enter one. Lisa refers some people who text or email ' +
    'in to the office line--for example, current residents or vendors.',
}

const holidays: Field<string> = {
  key: 'holidays',
  label: 'Holidays',
  isEditable: false,
  defaultToParentLabel: () => 'Holidays: see firm',
  overridenByChildLabel: 'Holidays: see property',
}

const linkToApply: Field<string> = EditBoxes.optUrl('applicationUrl', 'Link to apply?', 'Apply: ', 'http://...')

const showingPolicy: Field<string> = {
  key: 'showingPolicy',
  isEditable: true,
  options: Object.keys(ShowingPolicy),
  isOptional: true,
  textFunc: val => (val === 'DISABLED' ? 'Showings disabled' : 'Showing: ' + val),
  colorFunc: val => (val === 'DISABLED' ? 'coral' : undefined),
  editTextFunc: val => val || '',
  defaultToParentLabel: val => 'See firm: ' + val,
  overridenByChildLabel: 'Showing: see property',
}

const maxAcceptedPerShowing: Field<number> = {
  key: 'maxAcceptedPerShowing',
  isEditable: true,
  validator: validate.integer(n => n > 0).empty(''),
  textFunc: val => (val == null ? 'Max accepts per showing missing' : 'Max ' + val + ' accepts per showing'),
  editTextFunc: val => '' + (val || ''),
  defaultToParentLabel: () => 'Max per showing: see firm',
}

const maxSimultaneousShowings: Field<number> = {
  key: 'maxSimultaneousShowings',
  isEditable: true,
  validator: validate.integer(n => n > 0).empty(''),
  textFunc: val => (val == null ? 'Max simultaneous: no limit' : 'Max ' + val + ' simultaneous showings'),
  editTextFunc: val => '' + (val || ''),
}

const showingDurationMins: Field<number> = {
  key: 'showingDurationMins',
  isEditable: true,
  validator: validate.integer(n => n > 0).empty(''),
  textFunc: val => (val == null ? 'Showing duration missing' : 'Showing duration: ' + val + ' mins'),
  editTextFunc: val => '' + (val || ''),
  defaultToParentLabel: () => 'Showing duration: see firm',
  overridenByChildLabel: 'Showing duration: see tasks',
}

// TODO: make this editable, and display nicely.
const tourTypes: Field<TourType[]> = {
  key: 'tourTypes',
  isEditable: false,
  defaultToParentLabel: tourType => 'Tour Type: firm default ' + tourType,
  overridenByChildLabel: 'Tour Type: see property',
  textFunc: tourTypes => `tour types: ${Array.isArray(tourTypes) ? tourTypes.join(' ') : tourTypes}`,
  validator: validate.tourType(),
  placeholder: 'in-person, virtual, self-guided',
  editTextFunc: val => '' + (val || ''),
}

const getFieldsPropertyShort = (canEdit: boolean) => [
  areAgentsOnsite,
  section8,
  furnished,
  officePhone,
  propertyWebsite,
  floorPlans,
  showingPolicy,
  maxAcceptedPerShowing,
  Object.assign(maxSimultaneousShowings, { isEditable: canEdit }),
  showingDurationMins,
  Object.assign(tourTypes, { isEditable: canEdit }),
]

const fieldsPropertyLong: Field<any>[] = [
  showingHours,
  section8Text,
  qualText,
  petPolicyText,
  moveInCostText,
  discountsText,
  applicationDocsText,
  leaseTermsText,
  cosignerPolicyText,
  realtorText,
  {
    key: 'laundryText',
    label: 'Laundry',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'Washer and dryer are...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'utilitiesText',
    label: 'Utilities',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'Utilities are included...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'parkingText',
    label: 'Parking',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'Parking is available...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'selfGuidedMeetingDirections',
    label: 'Self-guided meeting directions',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'The front door code is...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'meetingDirections',
    label: 'Meeting directions',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'The meeting spot will be...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'handicappedUnitsText',
    label: 'Accessibility / ADA info',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'The first floor units are wheelchair accessible...',
    validator: validate.str().preventEmojis(),
  },
  {
    key: 'amenitiesText',
    label: 'Amenities',
    isEditable: true,
    editTextFunc: val => val,
    placeholder: 'Our community has a pool, an exercise facility...',
    validator: validate.str().preventEmojis(),
  },
  holidays,
]

const fieldsFirmShort: Field<any>[] = [
  officePhone,
  {
    key: 'maxAcceptedPerShowing',
    isEditable: true,
    textFunc: val => (val == null ? 'Unlimited accepts per showing' : 'Max ' + val + ' accepts per showing'),
    editTextFunc: nullToEmpty,
    validator: validate.integer(n => n >= 1).empty(''),
    placeholder: '10',
    overridenByChildLabel: 'Max per showing: see task',
  },
  showingDurationMins,
  EditBoxes.optUrl('propertyListingsUrl', 'Firm properties URL?', 'Properties: ', 'http://...'),
  section8,
  tourTypes,
  showingPolicy,
]

const fieldsFirmLong: Field<any>[] = [
  section8Text,
  qualText,
  petPolicyText,
  moveInCostText,
  discountsText,
  applicationDocsText,
  leaseTermsText,
  cosignerPolicyText,
  realtorText,
]

type ContextType = 'FIRM' | 'PROPERTY' | 'PROPERTY_MINIMAL'

type EditBoxDisplayType = 'small' | 'large'

export default class ContextEditBoxes extends React.PureComponent<{
  leasingTask: NonNullable<FetchInboxQuery['thread']>['leasingTask'] | NonNullable<FetchTasksPageQuery['task']>
  cascadeParent: boolean
  cascadeChild: boolean
  type: ContextType
  user: FetchUserContextQuery['self']
  onEdit: (key: string, value: any) => any
}> {
  render() {
    return (
      <div>
        {this.editBoxes('small')}
        {this.editBoxes('large')}
      </div>
    )
  }

  private getBoxesSpec(displayType: EditBoxDisplayType, canEdit: boolean) {
    const { leasingTask, type } = this.props
    const { leasingInfo, taskType } = leasingTask

    const hasNonApmProperties = leasingTask.firm.pmTypes.find(t => t !== 'APPFOLIO') != null

    let fields: Field<any>[]
    let model: any
    let cascadeParent: any
    let cascadeChild: any
    if (taskType === 'OPERATOR_DRAFT') {
      return { fields: [], model: {}, cascadeChild: null, cascadeParent: null }
    }
    if (type === 'PROPERTY') {
      assert(taskType === 'LEASING_PROPERTY', 'Expected a property task')
      fields = displayType === 'small' ? getFieldsPropertyShort(canEdit) : fieldsPropertyLong
      if (displayType === 'small' && hasNonApmProperties) {
        fields = fields.concat([linkToApply])
      }
      model = Object.assign({}, leasingInfo, leasingTask)
      cascadeParent = leasingTask.firm
    } else if (type === 'PROPERTY_MINIMAL') {
      assert(taskType === 'LEASING_PROPERTY', 'Expected a property task')
      fields = displayType === 'small' ? [areAgentsOnsite, tourTypes] : [showingHours]
      model = Object.assign({}, leasingInfo, leasingTask)
      cascadeParent = leasingTask.firm
    } else if (type === 'FIRM') {
      // FIRM
      fields = displayType === 'small' ? fieldsFirmShort : fieldsFirmLong
      model = leasingTask.firm
      cascadeChild = Object.assign({}, leasingInfo, leasingTask)
    } else {
      throw new Error('Unexpected ContextEditBoxes type ' + type)
    }

    return { fields, model, cascadeParent, cascadeChild }
  }

  private editBoxes = (type: EditBoxDisplayType) => {
    const { onEdit, user } = this.props
    const canEdit = user.permissions.canModifyNotes
    const { fields, model, cascadeParent, cascadeChild } = this.getBoxesSpec(type, canEdit)

    return (
      <EditBoxes
        model={model}
        disableEdit={!canEdit}
        cascadeParent={cascadeParent}
        cascadeChild={cascadeChild}
        displayType={type}
        fields={fields}
        onEdit={onEdit}
      />
    )
  }
}
